// src/pages/RatingPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Rating, Button, CircularProgress,
  Alert, Paper, Avatar, Divider, TextField
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import api from '../api/api';

export default function RatingPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [context, setContext] = useState(null); // { role, targetName }
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    const fetchContext = async () => {
      try {
        const { data } = await api.get(`/ratings/providers/context/${requestId}`);
        setContext(data);
      } catch (err) {
        setError('No se pudo cargar la solicitud para calificar.');
      } finally {
        setLoading(false);
      }
    };
    fetchContext();
  }, [requestId]);

  const handleSubmit = async () => {
    if (score === 0) {
      setError('Por favor seleccioná una puntuación.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post(`/ratings/providers/client/${requestId}`, { score, comment });
      setSuccess(true);
      setTimeout(() => navigate('/mis-solicitudes'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar la calificación.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress sx={{ color: '#163244' }} />
    </Box>
  );

  if (success) return (
    <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, textAlign: 'center' }}>
      <Alert severity="success" sx={{ mb: 2 }}>
        ¡Gracias por calificar! Redirigiendo...
      </Alert>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', mt: 6, px: 2 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#163244" mb={1}>
          Calificar {context?.role === 'client' ? 'al prestador' : 'al cliente'}
        </Typography>

        {context && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
            <Avatar
              src={context.targetAvatar || undefined}
              sx={{ bgcolor: '#163244', width: 52, height: 52 }}
            >
              {context.targetName?.[0]?.toUpperCase()}
            </Avatar>
            <Typography fontWeight={600} fontSize={17}>
              {context.targetName}
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        <Typography fontWeight={600} mb={1} color="#163244">
          Puntuación
        </Typography>
        <Rating
          value={score}
          onChange={(_, val) => setScore(val)}
          size="large"
          emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
          sx={{ mb: 3, '& .MuiRating-iconFilled': { color: '#E7A432' } }}
        />

        <Typography fontWeight={600} mb={1} color="#163244">
          Comentario (opcional)
        </Typography>
        <TextField
          multiline
          rows={4}
          fullWidth
          placeholder="Contá tu experiencia..."
          value={comment}
          onChange={e => setComment(e.target.value)}
          sx={{ mb: 2 }}
        />

        {error && <Alert severity="error" sx={{ mt: 1, mb: 2 }}>{error}</Alert>}

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || score === 0}
          sx={{
            mt: 1, py: 1.5, fontWeight: 700, fontSize: 15,
            bgcolor: '#163244', '&:hover': { bgcolor: '#0f2333' },
          }}
        >
          {submitting
            ? <CircularProgress size={22} sx={{ color: '#fff' }} />
            : 'Enviar calificación'}
        </Button>
      </Paper>
    </Box>
  );
}
